import type {
  EffectiveAssistantConfiguration,
  EffectiveAssistantConfigurationApiResource,
  PlatformAssistantConfiguration,
  PlatformAssistantConfigurationApiResource,
  PlatformAssistantConfigurationSaveApiResource,
  RevisionedPlatformAssistantConfiguration,
  RevisionedSave,
  WorkspaceAssistantConfiguration,
  WorkspaceAssistantConfigurationApiResource,
  WorkspaceAssistantOverride,
  WorkspaceAssistantOverrideApiResource,
  WorkspaceAssistantOverrideSaveApiResource,
  WorkspaceOwnerAssistantConfigurationUpdate,
} from './assistantConfigurationApi.types';

export function mapApiResourceToPlatformAssistantConfiguration(
  resource: PlatformAssistantConfigurationApiResource
): PlatformAssistantConfiguration {
  return {
    tone: resource.tone,
    replyLength: resource.reply_length,
    extraInstructions: resource.extra_instructions,
    allowedAccessAreas: resource.allowed_access_areas,
    ownersCanCustomize: resource.owners_can_customize,
    proactiveFeaturesEnabled: resource.proactive_features_enabled,
  };
}

export function mapApiResourceToRevisionedPlatformAssistantConfiguration(
  resource: PlatformAssistantConfigurationApiResource
): RevisionedPlatformAssistantConfiguration {
  return {
    configuration: mapApiResourceToPlatformAssistantConfiguration(resource),
    revision: resource.revision,
  };
}

export function mapPlatformAssistantConfigurationToApiResource(
  save: RevisionedSave<PlatformAssistantConfiguration>
): PlatformAssistantConfigurationSaveApiResource {
  const configuration = save.value;
  return {
    tone: configuration.tone,
    reply_length: configuration.replyLength,
    extra_instructions: configuration.extraInstructions,
    allowed_access_areas: configuration.allowedAccessAreas,
    owners_can_customize: configuration.ownersCanCustomize,
    proactive_features_enabled: configuration.proactiveFeaturesEnabled,
    expected_revision: save.expectedRevision,
  };
}

function mapApiResourceToWorkspaceAssistantOverride(
  resource: WorkspaceAssistantOverrideApiResource
): WorkspaceAssistantOverride {
  return {
    tone: resource.tone,
    replyLength: resource.reply_length,
    extraInstructions: resource.extra_instructions,
    allowedAccessAreas: resource.allowed_access_areas,
    disabledAccessAreas: resource.disabled_access_areas,
    ownerCanCustomize: resource.owner_can_customize,
    monthlyTokenBudget: resource.monthly_token_budget,
    proactiveFeaturesEnabled: resource.proactive_features_enabled,
  };
}

export function mapWorkspaceAssistantOverrideToApiResource(
  save: RevisionedSave<WorkspaceAssistantOverride>
): WorkspaceAssistantOverrideSaveApiResource {
  const override = save.value;
  return {
    tone: override.tone,
    reply_length: override.replyLength,
    extra_instructions: override.extraInstructions,
    allowed_access_areas: override.allowedAccessAreas,
    disabled_access_areas: override.disabledAccessAreas,
    owner_can_customize: override.ownerCanCustomize,
    monthly_token_budget: override.monthlyTokenBudget,
    proactive_features_enabled: override.proactiveFeaturesEnabled,
    expected_revision: save.expectedRevision,
  };
}

export function mapWorkspaceOwnerUpdateToApiResource(
  save: RevisionedSave<WorkspaceOwnerAssistantConfigurationUpdate>
): Record<string, unknown> {
  const update = save.value;
  return {
    tone: update.tone,
    reply_length: update.replyLength,
    extra_instructions: update.extraInstructions,
    disabled_access_areas: update.disabledAccessAreas,
    proactive_features_enabled: update.proactiveFeaturesEnabled,
    expected_revision: save.expectedRevision,
  };
}

function mapApiResourceToEffectiveAssistantConfiguration(
  resource: EffectiveAssistantConfigurationApiResource
): EffectiveAssistantConfiguration {
  return {
    tone: resource.tone,
    replyLength: resource.reply_length,
    allowedAccessAreas: resource.allowed_access_areas,
    activeAccessAreas: resource.active_access_areas,
    ownerCanCustomize: resource.owner_can_customize,
    proactiveFeaturesEnabled: resource.proactive_features_enabled,
  };
}

export function mapApiResourceToWorkspaceAssistantConfiguration(
  resource: WorkspaceAssistantConfigurationApiResource
): WorkspaceAssistantConfiguration {
  return {
    platform: mapApiResourceToPlatformAssistantConfiguration(resource.platform),
    workspace: mapApiResourceToWorkspaceAssistantOverride(resource.workspace),
    workspaceRevision: resource.workspace.revision,
    effective: mapApiResourceToEffectiveAssistantConfiguration(resource.effective),
    canCustomize: resource.can_customize,
    defaultMonthlyTokenBudget: resource.default_monthly_token_budget,
  };
}
